import React, { useState, useEffect } from 'react';
import AdminBlogEditor from '../components/AdminBlogEditor';
import AdminCampaigns from '../components/AdminCampaigns';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Users, Activity, TrendingUp, Sparkles, Settings, 
  FileText, Shield, BarChart3, Clock, Search, Eye, BookOpen, Upload, Trash2,
  CreditCard, Key, UserX, DollarSign, ChevronDown, ChevronUp, CheckCircle, XCircle
} from 'lucide-react';

const ADMIN_MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'pricing', label: 'Pricing Control', icon: DollarSign },
  { id: 'coupons', label: 'Coupons', icon: CreditCard },
  { id: 'paid-users', label: 'Paid Users', icon: CreditCard },
  { id: 'payments', label: 'Payment Tracking', icon: DollarSign },
  { id: 'analytics-biz', label: 'Business Analytics', icon: TrendingUp },
  { id: 'users', label: 'All Users', icon: Users },
  { id: 'knowledge-base', label: 'Knowledge Base', icon: BookOpen },
  { id: 'blog', label: 'Blog Engine', icon: BookOpen },
  { id: 'campaigns', label: 'Campaigns', icon: Activity },
  { id: 'settings', label: 'Settings', icon: Settings }
];

function AdminConsole() {
  const [activePanel, setActivePanel] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [knowledgeDocs, setKnowledgeDocs] = useState([]);
  const [paidUsers, setPaidUsers] = useState([]);
  const [payments, setPayments] = useState({ payments: [], stats: {} });
  const [bizAnalytics, setBizAnalytics] = useState(null);
  const [adminPricing, setAdminPricing] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: '', discount_type: 'percentage', discount_value: '', max_uses: 100 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('numerology');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const [ragDomainStats, setRagDomainStats] = useState(null);
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    youtube: ''
  });
  const navigate = useNavigate();

  // Restore admin token from localStorage on component mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      // No token found, redirect to admin login
      navigate('/admin');
      return;
    }
    // Set the token in axios default headers
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, [navigate]);

  useEffect(() => {
    if (activePanel === 'dashboard') {
      fetchDashboardStats();
    } else if (activePanel === 'users') {
      fetchUsers();
    } else if (activePanel === 'knowledge-base') {
      fetchKnowledgeDocs();
      fetchRagDomainStats();
    } else if (activePanel === 'settings') {
      fetchSettings();
    } else if (activePanel === 'paid-users') {
      fetchPaidUsers();
    } else if (activePanel === 'payments') {
      fetchPayments();
    } else if (activePanel === 'analytics-biz') {
      fetchBizAnalytics();
    } else if (activePanel === 'pricing') {
      fetchAdminPricing();
    } else if (activePanel === 'coupons') {
      fetchCoupons();
    }
  }, [activePanel]);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get('/api/admin/dashboard/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/admin/users?limit=50&search=${searchTerm}`);
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaidUsers = async () => {
    try {
      const res = await axios.get('/api/admin/paid-users');
      setPaidUsers(res.data.paid_users || []);
    } catch (err) { console.error('Error:', err); }
  };

  const fetchPayments = async () => {
    try {
      const res = await axios.get('/api/admin/payments');
      setPayments(res.data);
    } catch (err) { console.error('Error:', err); }
  };

  const fetchBizAnalytics = async () => {
    try {
      const res = await axios.get('/api/admin/analytics/business');
      setBizAnalytics(res.data);
    } catch (err) { console.error('Error:', err); }
  };

  const fetchAdminPricing = async () => {
    try {
      const res = await axios.get('/api/admin/pricing');
      setAdminPricing(res.data?.plans || {});
    } catch (err) { console.error('Error:', err); }
  };

  const savePricing = async () => {
    try {
      await axios.post('/api/admin/pricing', { plans: adminPricing, purchase_type: 'one_time' });
      setActionMessage('Pricing updated! Changes reflect on homepage immediately.');
    } catch (err) { setActionMessage('Failed to update pricing'); }
  };

  const fetchCoupons = async () => {
    try {
      const res = await axios.get('/api/admin/coupons');
      setCoupons(res.data?.coupons || []);
    } catch (err) { console.error('Error:', err); }
  };

  const createCoupon = async () => {
    try {
      const res = await axios.post('/api/admin/coupons', newCoupon);
      setActionMessage(res.data.message);
      setNewCoupon({ code: '', discount_type: 'percentage', discount_value: '', max_uses: 100 });
      fetchCoupons();
    } catch (err) { setActionMessage(err.response?.data?.detail || 'Failed'); }
  };

  const deleteCoupon = async (id) => {
    try {
      await axios.delete(`/api/admin/coupons/${id}`);
      fetchCoupons();
    } catch (err) { console.error(err); }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) { setActionMessage('Password must be 6+ characters'); return; }
    try {
      const res = await axios.post(`/api/admin/users/${userId}/reset-password`, { new_password: newPassword });
      setActionMessage(res.data.message);
      setResetPasswordModal(null);
      setNewPassword('');
    } catch (err) { setActionMessage(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`PERMANENTLY delete ${email} and ALL their data? This cannot be undone!`)) return;
    try {
      const res = await axios.delete(`/api/admin/users/${userId}`);
      setActionMessage(res.data.message);
      fetchUsers();
      fetchPaidUsers();
    } catch (err) { setActionMessage(err.response?.data?.detail || 'Failed'); }
  };

  const handleGrantAccess = async (orderId) => {
    if (!window.confirm('Manually grant subscription access for this payment?')) return;
    try {
      const res = await axios.post(`/api/admin/payments/${orderId}/grant-access`);
      setActionMessage(res.data.message);
      fetchPayments();
    } catch (err) { setActionMessage(err.response?.data?.detail || 'Failed'); }
  };

  const viewUserProfile = async (userId) => {
    try {
      const res = await axios.get(`/api/admin/users/${userId}/full-profile`);
      setSelectedUser(res.data);
    } catch (err) { console.error('Error:', err); }
  };


  const fetchKnowledgeDocs = async () => {
    setLoading(true);
    try {
      const url = selectedCategory === 'all' 
        ? '/api/admin/knowledge-base/documents'
        : `/api/admin/knowledge-base/documents?category=${selectedCategory}`;
      const response = await axios.get(url);
      setKnowledgeDocs(response.data.documents || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRagDomainStats = async () => {
    try {
      const response = await axios.get('/api/admin/knowledge-base/rag-stats');
      setRagDomainStats(response.data);
    } catch (err) {
      console.error('RAG stats error:', err);
    }
  };


  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    const filesToUpload = uploadFiles.length > 0 ? uploadFiles : (uploadFile ? [uploadFile] : []);
    
    if (filesToUpload.length === 0) {
      alert('Please select at least one file');
      return;
    }

    setLoading(true);
    setUploadProgress({});
    const results = [];
    
    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      const progressKey = file.name;
      
      try {
        setUploadProgress(prev => ({ ...prev, [progressKey]: `Uploading (${i+1}/${filesToUpload.length})...` }));
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', uploadCategory);
        formData.append('description', uploadDescription);

        const response = await axios.post('/api/admin/knowledge-base/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
        
        const result = response.data;
        
        results.push({
          file: file.name,
          success: true,
          rag_indexed: result.rag_indexed,
          rag_status: result.rag_status || 'unknown',
          chunks: result.chunks_created || 0,
          pages: result.page_count || 0,
          domain_distribution: result.domain_distribution || {}
        });
        
        setUploadProgress(prev => ({ 
          ...prev, 
          [progressKey]: result.rag_status === 'indexing' 
            ? 'Uploaded - indexing in background...' 
            : result.rag_indexed 
              ? `Indexed (${result.chunks_created || 0} chunks)` 
              : 'Uploaded' 
        }));
        
      } catch (err) {
        console.error(`Upload error for ${file.name}:`, err);
        const errorMsg = err.response?.data?.detail || err.message || 'Upload failed';
        
        results.push({
          file: file.name,
          success: false,
          error: errorMsg
        });
        
        setUploadProgress(prev => ({ ...prev, [progressKey]: `Failed: ${errorMsg}` }));
      }
    }
    
    // Show summary
    const successCount = results.filter(r => r.success).length;
    const indexingCount = results.filter(r => r.rag_status === 'indexing').length;
    const failedCount = results.filter(r => !r.success).length;
    
    let summaryMsg = `Upload Summary:\n\n`;
    summaryMsg += `Total files: ${filesToUpload.length}\n`;
    summaryMsg += `Uploaded: ${successCount}\n`;
    if (indexingCount > 0) {
      summaryMsg += `RAG Indexing: ${indexingCount} (processing in background)\n`;
    }
    
    if (failedCount > 0) {
      summaryMsg += `Failed: ${failedCount}\n`;
      results.filter(r => !r.success).forEach(r => {
        summaryMsg += `  - ${r.file}: ${r.error}\n`;
      });
    }
    
    if (indexingCount > 0) {
      summaryMsg += `\nRefresh the page in 1-2 minutes to see indexing results.`;
    }
    
    alert(summaryMsg);
    
    setUploadFile(null);
    setUploadFiles([]);
    setUploadDescription('');
    setUploadProgress({});
    setLoading(false);
    
    // Refresh document list and stats
    fetchKnowledgeDocs();
    fetchRagDomainStats();
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(`/api/admin/knowledge-base/documents/${docId}`);
      alert('Document deleted successfully!');
      fetchKnowledgeDocs();
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to delete document');
    }
  };


  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/settings/social-links');
      setSocialLinks(response.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSaveSocialLinks = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post('/api/admin/settings', {
        social_links: socialLinks
      });
      alert('Social media links saved successfully!');
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };



  const renderPanelContent = () => {
    switch (activePanel) {
      case 'dashboard':
        return stats ? (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Admin Dashboard</h2>
            
            {/* Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
              <div className="card" style={{ padding: '2rem', background: 'linear-gradient(135deg, rgba(0, 217, 255, 0.1) 0%, rgba(0, 217, 255, 0.05) 100%)', border: '2px solid #00D9FF' }}>
                <Users size={32} color="#00D9FF" style={{ marginBottom: '1rem' }} />
                <div style={{ fontSize: '3rem', fontWeight: 700, color: '#00D9FF', marginBottom: '0.5rem' }}>
                  {stats.users.total}
                </div>
                <p style={{ fontSize: '1rem', color: '#9CA3AF' }}>Total Users</p>
                <p style={{ fontSize: '0.875rem', color: '#00D9FF', marginTop: '0.5rem' }}>
                  +{stats.users.today} today • +{stats.users.this_month} this month
                </p>
              </div>

              <div className="card" style={{ padding: '2rem' }}>
                <Activity size={32} color="#10B981" style={{ marginBottom: '1rem' }} />
                <div style={{ fontSize: '3rem', fontWeight: 700, color: '#10B981', marginBottom: '0.5rem' }}>
                  {stats.users.profiles_completed}
                </div>
                <p style={{ fontSize: '1rem', color: '#9CA3AF' }}>Profiles Completed</p>
              </div>

              <div className="card" style={{ padding: '2rem' }}>
                <Sparkles size={32} color="#7C3AED" style={{ marginBottom: '1rem' }} />
                <div style={{ fontSize: '3rem', fontWeight: 700, color: '#7C3AED', marginBottom: '0.5rem' }}>
                  {stats.readings.total}
                </div>
                <p style={{ fontSize: '1rem', color: '#9CA3AF' }}>Total Readings</p>
                <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#9CA3AF' }}>
                  <div>Numerology: {stats.readings.numerology}</div>
                  <div>Vastu: {stats.readings.vastu}</div>
                  <div>Astrology: {stats.readings.astrology}</div>
                </div>
              </div>

              <div className="card" style={{ padding: '2rem' }}>
                <Sparkles size={32} color="#F59E0B" style={{ marginBottom: '1rem' }} />
                <div style={{ fontSize: '3rem', fontWeight: 700, color: '#F59E0B', marginBottom: '0.5rem' }}>
                  {stats.ai_usage.total_chats}
                </div>
                <p style={{ fontSize: '1rem', color: '#9CA3AF' }}>AI Chats</p>
                <p style={{ fontSize: '0.875rem', color: '#F59E0B', marginTop: '0.5rem' }}>
                  {stats.ai_usage.recent_24h} in last 24h
                </p>
              </div>
            </div>
          </div>
        ) : <div>Loading...</div>;

      case 'pricing':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Pricing Control</h2>
            <p style={{ color: '#9CA3AF', marginBottom: '2rem' }}>Set prices for each module. Changes reflect on homepage immediately. All prices in ₹ (ONE-TIME purchase).</p>
            {actionMessage && <div style={{ padding: '1rem', background: '#10B98120', border: '1px solid #10B981', borderRadius: '8px', marginBottom: '1rem', color: '#10B981' }}>{actionMessage}</div>}
            {adminPricing && (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {Object.entries(adminPricing).map(([planId, plan]) => (
                  <div key={planId} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: '#151827', borderRadius: '10px', border: '1px solid #2D3748' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{plan.name || planId}</p>
                      <p style={{ fontSize: '0.75rem', color: '#6B7280' }}>{planId}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ color: '#9CA3AF' }}>₹</span>
                      <input
                        type="number"
                        value={plan.display_price || ''}
                        onChange={e => setAdminPricing(prev => ({ ...prev, [planId]: { ...prev[planId], display_price: parseInt(e.target.value) || 0, price: (parseInt(e.target.value) || 0) * 100 } }))}
                        style={{ width: '100px', padding: '0.5rem', background: '#0A0F2A', border: '1px solid #374151', borderRadius: '6px', color: '#FFF', textAlign: 'right', fontSize: '1.125rem' }}
                      />
                      <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>one-time</span>
                    </div>
                  </div>
                ))}
                <button onClick={savePricing} style={{ padding: '1rem', background: 'linear-gradient(135deg, #10B981, #059669)', border: 'none', borderRadius: '10px', color: '#FFF', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', marginTop: '1rem' }}>
                  Save Pricing Changes
                </button>
              </div>
            )}
          </div>
        );

      case 'coupons':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Coupon Engine</h2>
            <p style={{ color: '#9CA3AF', marginBottom: '1.5rem' }}>Create discount coupons for festive or promotional offers.</p>
            {actionMessage && <div style={{ padding: '1rem', background: '#10B98120', border: '1px solid #10B981', borderRadius: '8px', marginBottom: '1rem', color: '#10B981' }}>{actionMessage}</div>}
            
            {/* Create Coupon */}
            <div style={{ padding: '1.5rem', background: '#151827', borderRadius: '12px', border: '1px solid #2D3748', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Create New Coupon</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Code (leave blank for auto)</label>
                  <input type="text" value={newCoupon.code} onChange={e => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} placeholder="e.g., DIWALI50" style={{ width: '100%', padding: '0.625rem', background: '#0A0F2A', border: '1px solid #374151', borderRadius: '6px', color: '#FFF' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Discount Type</label>
                  <select value={newCoupon.discount_type} onChange={e => setNewCoupon({ ...newCoupon, discount_type: e.target.value })} style={{ width: '100%', padding: '0.625rem', background: '#0A0F2A', border: '1px solid #374151', borderRadius: '6px', color: '#FFF' }}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="flat">Flat Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Discount Value</label>
                  <input type="number" value={newCoupon.discount_value} onChange={e => setNewCoupon({ ...newCoupon, discount_value: e.target.value })} placeholder={newCoupon.discount_type === 'percentage' ? 'e.g., 20' : 'e.g., 50'} style={{ width: '100%', padding: '0.625rem', background: '#0A0F2A', border: '1px solid #374151', borderRadius: '6px', color: '#FFF' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Max Uses</label>
                  <input type="number" value={newCoupon.max_uses} onChange={e => setNewCoupon({ ...newCoupon, max_uses: parseInt(e.target.value) || 100 })} style={{ width: '100%', padding: '0.625rem', background: '#0A0F2A', border: '1px solid #374151', borderRadius: '6px', color: '#FFF' }} />
                </div>
              </div>
              <button onClick={createCoupon} style={{ marginTop: '1rem', padding: '0.75rem 2rem', background: '#7C3AED', border: 'none', borderRadius: '8px', color: '#FFF', fontWeight: 600, cursor: 'pointer' }}>
                Create Coupon
              </button>
            </div>
            
            {/* Existing Coupons */}
            <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Active Coupons</h3>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {coupons.filter(c => c.is_active).map(c => (
                <div key={c._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', background: '#151827', borderRadius: '10px', border: '1px solid #2D3748' }}>
                  <div>
                    <span style={{ fontSize: '1.125rem', fontWeight: 700, color: '#F59E0B', letterSpacing: '1px' }}>{c.code}</span>
                    <span style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#10B981' }}>
                      {c.discount_type === 'percentage' ? `${c.discount_value}% off` : `₹${c.discount_value} off`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>Used: {c.used_count || 0}/{c.max_uses}</span>
                    <button onClick={() => deleteCoupon(c._id)} style={{ background: 'none', border: '1px solid #EF4444', color: '#EF4444', padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>Deactivate</button>
                  </div>
                </div>
              ))}
              {coupons.filter(c => c.is_active).length === 0 && <p style={{ color: '#6B7280', textAlign: 'center', padding: '2rem' }}>No active coupons</p>}
            </div>
          </div>
        );

      case 'paid-users':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Paid Users Tracking</h2>
            {actionMessage && <div style={{ padding: '1rem', background: '#10B98120', border: '1px solid #10B981', borderRadius: '8px', marginBottom: '1rem', color: '#10B981' }}>{actionMessage}</div>}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #2D3748' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#9CA3AF' }}>User</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#9CA3AF' }}>Email</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', color: '#9CA3AF' }}>Active Plans</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', color: '#9CA3AF' }}>Total Paid</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', color: '#9CA3AF' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paidUsers.map((u, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #1E2139' }}>
                      <td style={{ padding: '0.75rem' }}>{u.full_name}</td>
                      <td style={{ padding: '0.75rem', color: '#00D9FF' }}>{u.email}</td>
                      <td style={{ padding: '0.75rem' }}>
                        {(u.active_plans || []).map((p, j) => (
                          <span key={j} style={{ display: 'inline-block', padding: '0.125rem 0.5rem', background: '#7C3AED20', color: '#A78BFA', borderRadius: '4px', fontSize: '0.75rem', marginRight: '0.25rem' }}>{p}</span>
                        ))}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', color: '#10B981', fontWeight: 600 }}>
                        {u.total_paid ? `₹${(u.total_paid / 100).toFixed(0)}` : '₹0'}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <button onClick={() => viewUserProfile(u.user_id)} style={{ background: 'none', border: '1px solid #3B82F6', color: '#3B82F6', padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', marginRight: '0.25rem' }}>Profile</button>
                        <button onClick={() => setResetPasswordModal(u)} style={{ background: 'none', border: '1px solid #F59E0B', color: '#F59E0B', padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', marginRight: '0.25rem' }}>Reset Pass</button>
                        <button onClick={() => handleDeleteUser(u.user_id, u.email)} style={{ background: 'none', border: '1px solid #EF4444', color: '#EF4444', padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {paidUsers.length === 0 && <p style={{ textAlign: 'center', color: '#6B7280', padding: '2rem' }}>No paid users found</p>}
            </div>
            {/* Reset Password Modal */}
            {resetPasswordModal && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                <div style={{ background: '#151827', padding: '2rem', borderRadius: '16px', width: '400px', border: '2px solid #2D3748' }}>
                  <h3 style={{ marginBottom: '1rem' }}>Reset Password for {resetPasswordModal.email}</h3>
                  <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password (min 6 chars)" style={{ width: '100%', padding: '0.75rem', background: '#0A0F2A', border: '1px solid #374151', borderRadius: '8px', color: '#FFF', marginBottom: '1rem' }} />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => handleResetPassword(resetPasswordModal.user_id)} style={{ flex: 1, padding: '0.75rem', background: '#F59E0B', border: 'none', borderRadius: '8px', color: '#000', fontWeight: 600, cursor: 'pointer' }}>Reset</button>
                    <button onClick={() => { setResetPasswordModal(null); setNewPassword(''); }} style={{ flex: 1, padding: '0.75rem', background: '#374151', border: 'none', borderRadius: '8px', color: '#FFF', cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
            {/* User Profile Modal */}
            {selectedUser && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                <div style={{ background: '#151827', padding: '2rem', borderRadius: '16px', width: '600px', maxHeight: '80vh', overflowY: 'auto', border: '2px solid #2D3748' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h3>User Profile</h3>
                    <button onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '1.5rem' }}>&times;</button>
                  </div>
                  <div style={{ display: 'grid', gap: '0.75rem', fontSize: '0.875rem' }}>
                    <div style={{ padding: '1rem', background: '#0A0F2A', borderRadius: '8px' }}>
                      <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Name</p>
                      <p style={{ fontWeight: 600 }}>{selectedUser.user?.full_name || 'N/A'}</p>
                    </div>
                    <div style={{ padding: '1rem', background: '#0A0F2A', borderRadius: '8px' }}>
                      <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Email</p>
                      <p style={{ color: '#00D9FF' }}>{selectedUser.user?.email || 'N/A'}</p>
                    </div>
                    {selectedUser.profile && (
                      <>
                        <div style={{ padding: '1rem', background: '#0A0F2A', borderRadius: '8px' }}>
                          <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Birth Details</p>
                          <p>{selectedUser.profile.birth_date} at {selectedUser.profile.birth_time}</p>
                          <p style={{ color: '#9CA3AF' }}>{selectedUser.profile.birth_place}</p>
                        </div>
                        <div style={{ padding: '1rem', background: '#0A0F2A', borderRadius: '8px' }}>
                          <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Gender</p>
                          <p>{selectedUser.profile.gender || 'N/A'}</p>
                        </div>
                      </>
                    )}
                    <div style={{ padding: '1rem', background: '#0A0F2A', borderRadius: '8px' }}>
                      <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Active Subscriptions</p>
                      {(selectedUser.subscriptions || []).filter(s => s.status === 'ACTIVE').map((s, i) => (
                        <span key={i} style={{ display: 'inline-block', padding: '0.25rem 0.75rem', background: '#10B98120', color: '#10B981', borderRadius: '6px', margin: '0.25rem', fontSize: '0.8125rem' }}>{s.plan_name || s.plan_id}</span>
                      ))}
                    </div>
                    <div style={{ padding: '1rem', background: '#0A0F2A', borderRadius: '8px' }}>
                      <p style={{ color: '#6B7280', marginBottom: '0.25rem' }}>Usage Stats</p>
                      <p>Numerology: {selectedUser.stats?.numerology_readings || 0} | Astrology: {selectedUser.stats?.astrology_readings || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'payments':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Payment Tracking</h2>
            {actionMessage && <div style={{ padding: '1rem', background: '#10B98120', border: '1px solid #10B981', borderRadius: '8px', marginBottom: '1rem', color: '#10B981' }}>{actionMessage}</div>}
            {/* Payment Stats */}
            {payments.stats && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1.25rem', background: '#151827', borderRadius: '12px', textAlign: 'center', border: '1px solid #10B98140' }}>
                  <p style={{ fontSize: '1.75rem', fontWeight: 700, color: '#10B981' }}>{payments.stats.total_revenue_inr}</p>
                  <p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>Total Revenue</p>
                </div>
                <div style={{ padding: '1.25rem', background: '#151827', borderRadius: '12px', textAlign: 'center' }}>
                  <p style={{ fontSize: '1.75rem', fontWeight: 700, color: '#00D9FF' }}>{payments.stats.completed || 0}</p>
                  <p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>Completed</p>
                </div>
                <div style={{ padding: '1.25rem', background: '#151827', borderRadius: '12px', textAlign: 'center' }}>
                  <p style={{ fontSize: '1.75rem', fontWeight: 700, color: '#F59E0B' }}>{payments.stats.pending || 0}</p>
                  <p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>Pending</p>
                </div>
                <div style={{ padding: '1.25rem', background: '#151827', borderRadius: '12px', textAlign: 'center' }}>
                  <p style={{ fontSize: '1.75rem', fontWeight: 700, color: '#EF4444' }}>{payments.stats.failed || 0}</p>
                  <p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>Failed</p>
                </div>
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #2D3748' }}>
                    <th style={{ padding: '0.625rem', textAlign: 'left', color: '#9CA3AF' }}>Email</th>
                    <th style={{ padding: '0.625rem', textAlign: 'left', color: '#9CA3AF' }}>Plan</th>
                    <th style={{ padding: '0.625rem', textAlign: 'right', color: '#9CA3AF' }}>Amount</th>
                    <th style={{ padding: '0.625rem', textAlign: 'center', color: '#9CA3AF' }}>Status</th>
                    <th style={{ padding: '0.625rem', textAlign: 'center', color: '#9CA3AF' }}>Account</th>
                    <th style={{ padding: '0.625rem', textAlign: 'left', color: '#9CA3AF' }}>Date</th>
                    <th style={{ padding: '0.625rem', textAlign: 'center', color: '#9CA3AF' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(payments.payments || []).map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #1E2139' }}>
                      <td style={{ padding: '0.625rem', color: '#00D9FF' }}>{p.user_email}</td>
                      <td style={{ padding: '0.625rem' }}>{p.plan_name || p.plan_id}</td>
                      <td style={{ padding: '0.625rem', textAlign: 'right', fontWeight: 600 }}>₹{((p.amount || 0) / 100).toFixed(0)}</td>
                      <td style={{ padding: '0.625rem', textAlign: 'center' }}>
                        <span style={{ padding: '0.125rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, background: p.status === 'COMPLETED' || p.status === 'SUCCESS' ? '#10B98120' : p.status === 'PENDING' ? '#F59E0B20' : '#EF444420', color: p.status === 'COMPLETED' || p.status === 'SUCCESS' ? '#10B981' : p.status === 'PENDING' ? '#F59E0B' : '#EF4444' }}>
                          {p.status}
                        </span>
                      </td>
                      <td style={{ padding: '0.625rem', textAlign: 'center' }}>
                        {p.has_account ? (p.has_subscription ? <CheckCircle size={16} color="#10B981" /> : <span style={{ color: '#F59E0B', fontSize: '0.75rem' }}>No Sub</span>) : <XCircle size={16} color="#EF4444" />}
                      </td>
                      <td style={{ padding: '0.625rem', color: '#6B7280' }}>{p.created_at ? new Date(p.created_at).toLocaleDateString() : 'N/A'}</td>
                      <td style={{ padding: '0.625rem', textAlign: 'center' }}>
                        {(p.status === 'PENDING' || (p.status === 'COMPLETED' && !p.has_subscription)) && (
                          <button onClick={() => handleGrantAccess(p.merchant_order_id)} style={{ background: '#10B981', border: 'none', color: '#FFF', padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600 }}>Grant Access</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'analytics-biz':
        return bizAnalytics ? (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Business Analytics</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
              {/* Users */}
              <div style={{ padding: '1.5rem', background: '#151827', borderRadius: '16px', border: '2px solid #3B82F640' }}>
                <h3 style={{ color: '#3B82F6', marginBottom: '1rem', fontSize: '1.25rem' }}>Users</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div><p style={{ fontSize: '2rem', fontWeight: 700 }}>{bizAnalytics.users?.total}</p><p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>Total Users</p></div>
                  <div><p style={{ fontSize: '2rem', fontWeight: 700, color: '#10B981' }}>{bizAnalytics.users?.this_month}</p><p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>This Month</p></div>
                  <div><p style={{ fontSize: '1.25rem', fontWeight: 600 }}>{bizAnalytics.users?.profiles_completed}</p><p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>Profiles Done</p></div>
                  <div><p style={{ fontSize: '1.25rem', fontWeight: 600, color: '#F59E0B' }}>{bizAnalytics.users?.conversion_rate}</p><p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>Conversion Rate</p></div>
                </div>
              </div>
              {/* Revenue */}
              <div style={{ padding: '1.5rem', background: '#151827', borderRadius: '16px', border: '2px solid #10B98140' }}>
                <h3 style={{ color: '#10B981', marginBottom: '1rem', fontSize: '1.25rem' }}>Revenue</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div><p style={{ fontSize: '2rem', fontWeight: 700, color: '#10B981' }}>{bizAnalytics.revenue?.total_inr}</p><p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>Total Revenue</p></div>
                  <div><p style={{ fontSize: '2rem', fontWeight: 700, color: '#00D9FF' }}>{bizAnalytics.revenue?.this_month_inr}</p><p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>This Month</p></div>
                  <div><p style={{ fontSize: '1.25rem', fontWeight: 600, color: '#F59E0B' }}>{bizAnalytics.revenue?.pending_count} ({bizAnalytics.revenue?.pending_amount_inr})</p><p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>Pending Payments</p></div>
                  <div><p style={{ fontSize: '1.25rem', fontWeight: 600 }}>{bizAnalytics.revenue?.avg_revenue_per_user}</p><p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>Avg/User</p></div>
                </div>
              </div>
              {/* Subscriptions */}
              <div style={{ padding: '1.5rem', background: '#151827', borderRadius: '16px', border: '2px solid #7C3AED40' }}>
                <h3 style={{ color: '#7C3AED', marginBottom: '1rem', fontSize: '1.25rem' }}>Subscriptions</h3>
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '2rem', fontWeight: 700 }}>{bizAnalytics.subscriptions?.active}</p>
                  <p style={{ color: '#6B7280', fontSize: '0.8125rem' }}>Active Subscriptions</p>
                </div>
                {bizAnalytics.subscriptions?.plan_distribution && Object.entries(bizAnalytics.subscriptions.plan_distribution).map(([plan, count]) => (
                  <div key={plan} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #1E2139' }}>
                    <span style={{ color: '#D1D5DB' }}>{plan}</span>
                    <span style={{ fontWeight: 600, color: '#A78BFA' }}>{count}</span>
                  </div>
                ))}
              </div>
              {/* Usage */}
              <div style={{ padding: '1.5rem', background: '#151827', borderRadius: '16px', border: '2px solid #FB923C40' }}>
                <h3 style={{ color: '#FB923C', marginBottom: '1rem', fontSize: '1.25rem' }}>Platform Usage</h3>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#9CA3AF' }}>Numerology Readings</span><span style={{ fontWeight: 600 }}>{bizAnalytics.usage?.numerology_readings}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#9CA3AF' }}>Astrology Readings</span><span style={{ fontWeight: 600 }}>{bizAnalytics.usage?.astrology_readings}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#9CA3AF' }}>AI Chats</span><span style={{ fontWeight: 600 }}>{bizAnalytics.usage?.ai_chats}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#9CA3AF' }}>Vastu Readings</span><span style={{ fontWeight: 600 }}>{bizAnalytics.usage?.vastu_readings}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '2px solid #2D3748' }}><span style={{ color: '#FB923C', fontWeight: 600 }}>Total Interactions</span><span style={{ fontWeight: 700, color: '#FB923C' }}>{bizAnalytics.usage?.total_interactions}</span></div>
                </div>
              </div>
            </div>
          </div>
        ) : <div style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF' }}>Loading analytics...</div>;

      case 'users':
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '2rem' }}>User Management</h2>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input"
                  style={{ width: '300px' }}
                />
                <button onClick={fetchUsers} className="btn btn-primary">
                  <Search size={18} style={{ marginRight: '0.5rem' }} />
                  Search
                </button>
              </div>
            </div>

            <div className="card">
              <table style={{ width: '100%', fontSize: '0.9375rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #2D3748' }}>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Created</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user._id} style={{ borderBottom: '1px solid #2D3748' }}>
                      <td style={{ padding: '1rem' }}>{user.email}</td>
                      <td style={{ padding: '1rem' }}>{user.full_name || 'N/A'}</td>
                      <td style={{ padding: '1rem' }}>
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <button
                          onClick={() => alert('View details for: ' + user.email)}
                          className="btn btn-ghost"
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                        >
                          <Eye size={16} style={{ marginRight: '0.5rem' }} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {users.length === 0 && (
                <p style={{ textAlign: 'center', padding: '2rem', color: '#9CA3AF' }}>
                  {loading ? 'Loading...' : 'No users found'}
                </p>
              )}
            </div>
          </div>
        );


      case 'knowledge-base':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Knowledge Base</h2>
            
            {/* RAG Domain Stats */}
            {ragDomainStats && (
              <div style={{ marginBottom: '2rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                {Object.entries(ragDomainStats.domain_stats || {}).map(([domain, count]) => (
                  <div key={domain} className="card" data-testid={`rag-stat-${domain}`} style={{ padding: '1.25rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700,
                      color: domain === 'numerology' ? '#8B5CF6' :
                             domain === 'astrology' ? '#3B82F6' :
                             domain === 'vastu' ? '#10B981' : '#FB923C'
                    }}>
                      {count}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.25rem' }}>
                      {domain} vectors
                    </div>
                  </div>
                ))}
                <div className="card" style={{ padding: '1.25rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#00D9FF' }}>
                    {ragDomainStats.total_vectors || 0}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.25rem' }}>
                    Total Vectors
                  </div>
                </div>
              </div>
            )}
            
            {/* Upload Section */}
            <div className="card" style={{ marginBottom: '2rem', padding: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#00D9FF' }}>
                <Upload size={24} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Upload Training Document
              </h3>
              
              <form onSubmit={handleFileUpload}>
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9CA3AF' }}>
                      Category
                    </label>
                    <select
                      value={uploadCategory}
                      onChange={(e) => setUploadCategory(e.target.value)}
                      className="form-input"
                      style={{ width: '100%' }}
                    >
                      <option value="numerology">Numerology</option>
                      <option value="astrology">Astrology</option>
                      <option value="vastu">Vastu</option>
                      <option value="remedies">Remedies</option>
                      <option value="general">General</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9CA3AF' }}>
                      Files (PDF, TXT, DOCX, PNG, JPG) - Select Multiple
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.txt,.docx,.png,.jpg,.jpeg,.webp"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        setUploadFiles(files);
                        if (files.length === 1) setUploadFile(files[0]);
                      }}
                      className="form-input"
                      style={{ width: '100%' }}
                      required
                    />
                    {uploadFiles.length > 0 && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#10B981' }}>
                        ✅ {uploadFiles.length} file(s) selected
                      </div>
                    )}
                    {Object.keys(uploadProgress).length > 0 && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#1E2139', borderRadius: '8px' }}>
                        <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#00D9FF' }}>
                          Upload Progress:
                        </p>
                        {Object.entries(uploadProgress).map(([filename, status]) => (
                          <div key={filename} style={{ fontSize: '0.75rem', color: '#9CA3AF', marginBottom: '0.25rem' }}>
                            {filename}: {status}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9CA3AF' }}>
                      Description (Optional)
                    </label>
                    <textarea
                      value={uploadDescription}
                      onChange={(e) => setUploadDescription(e.target.value)}
                      placeholder="Brief description of the document..."
                      className="form-input"
                      style={{ width: '100%', minHeight: '100px' }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={(uploadFiles.length === 0 && !uploadFile) || loading}
                    className="btn btn-primary"
                    style={{ width: 'fit-content' }}
                  >
                    {loading ? `Uploading ${uploadFiles.length || 1} file(s)...` : `Upload ${uploadFiles.length || 1} Document(s)`}
                  </button>
                </div>
              </form>
            </div>

            {/* Filter by Category */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ color: '#9CA3AF' }}>Filter by:</span>
              {['all', 'numerology', 'astrology', 'vastu', 'remedies', 'general'].map(cat => (
                <button
                  key={cat}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setTimeout(fetchKnowledgeDocs, 100);
                  }}
                  className={selectedCategory === cat ? 'btn btn-primary' : 'btn btn-ghost'}
                  style={{ textTransform: 'capitalize', padding: '0.5rem 1rem' }}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Documents List */}
            <div className="card">
              <table style={{ width: '100%', fontSize: '0.9375rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #2D3748' }}>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Filename</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Category</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>RAG Status</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Uploaded</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {knowledgeDocs.map(doc => (
                    <tr key={doc._id} style={{ borderBottom: '1px solid #2D3748' }}>
                      <td style={{ padding: '1rem' }}>{doc.original_filename}</td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          background: 'rgba(0, 217, 255, 0.1)',
                          color: '#00D9FF'
                        }}>
                          {doc.category}
                        </span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {doc.rag_indexed ? (
                          <div>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              background: 'rgba(16, 185, 129, 0.1)',
                              color: '#10B981',
                              fontWeight: 600
                            }}>
                              INDEXED
                            </span>
                            <div style={{ fontSize: '0.625rem', color: '#6B7280', marginTop: '0.25rem' }}>
                              {doc.rag_chunks || 0} chunks
                            </div>
                            {doc.domain_distribution && Object.keys(doc.domain_distribution).length > 0 && (
                              <div style={{ marginTop: '0.25rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                {Object.entries(doc.domain_distribution).map(([domain, count]) => (
                                  <span key={domain} style={{
                                    padding: '0.125rem 0.375rem',
                                    borderRadius: '4px',
                                    fontSize: '0.5625rem',
                                    background: domain === 'numerology' ? 'rgba(139,92,246,0.15)' :
                                               domain === 'astrology' ? 'rgba(59,130,246,0.15)' :
                                               domain === 'vastu' ? 'rgba(16,185,129,0.15)' :
                                               'rgba(251,146,60,0.15)',
                                    color: domain === 'numerology' ? '#8B5CF6' :
                                           domain === 'astrology' ? '#3B82F6' :
                                           domain === 'vastu' ? '#10B981' : '#FB923C',
                                  }}>
                                    {domain}: {count}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : doc.rag_status === 'indexing' || doc.rag_status === 'pending' ? (
                          <div>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              background: 'rgba(59, 130, 246, 0.1)',
                              color: '#3B82F6',
                              fontWeight: 600
                            }}>
                              INDEXING...
                            </span>
                            <div style={{ fontSize: '0.625rem', color: '#6B7280', marginTop: '0.25rem' }}>
                              Processing in background
                            </div>
                          </div>
                        ) : doc.rag_status === 'failed' ? (
                          <div>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.75rem',
                              background: 'rgba(239, 68, 68, 0.1)',
                              color: '#EF4444',
                              fontWeight: 600
                            }}>
                              FAILED
                            </span>
                            {doc.rag_error && (
                              <div style={{ fontSize: '0.5625rem', color: '#EF4444', marginTop: '0.25rem', maxWidth: '200px', wordBreak: 'break-word' }}>
                                {doc.rag_error.substring(0, 120)}
                              </div>
                            )}
                            <button
                              onClick={async () => {
                                try {
                                  await axios.post(`/api/admin/knowledge-base/documents/${doc._id}/reindex`);
                                  alert('Re-indexing started. Refresh in a minute.');
                                  fetchKnowledgeDocs();
                                } catch(err) { alert('Re-index failed: ' + (err.response?.data?.detail || err.message)); }
                              }}
                              style={{
                                display: 'block', marginTop: '0.25rem', fontSize: '0.625rem',
                                color: '#3B82F6', background: 'none', border: 'none', cursor: 'pointer',
                                textDecoration: 'underline', padding: 0
                              }}
                            >
                              Retry Index
                            </button>
                          </div>
                        ) : (
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            background: 'rgba(251, 146, 60, 0.1)',
                            color: '#FB923C'
                          }}>
                            ⚠️ NOT INDEXED
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '1rem', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.description || 'N/A'}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <button
                          onClick={() => handleDeleteDocument(doc._id)}
                          className="btn btn-ghost"
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', color: '#DC2626' }}
                        >
                          <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {knowledgeDocs.length === 0 && (
                <p style={{ textAlign: 'center', padding: '2rem', color: '#9CA3AF' }}>
                  {loading ? 'Loading...' : 'No documents uploaded yet'}
                </p>
              )}
            </div>
          </div>
        );

      case 'settings':
        return (
          <div>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>System Settings</h2>
            
            {/* Social Media Links */}
            <div className="card" style={{ marginBottom: '2rem', padding: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#00D9FF' }}>
                Social Media Links
              </h3>
              <p style={{ fontSize: '0.9375rem', color: '#9CA3AF', marginBottom: '1.5rem' }}>
                Configure social media links that will appear on the homepage footer
              </p>
              
              <form onSubmit={handleSaveSocialLinks}>
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9CA3AF' }}>
                      Facebook URL
                    </label>
                    <input
                      type="url"
                      value={socialLinks.facebook}
                      onChange={(e) => setSocialLinks({ ...socialLinks, facebook: e.target.value })}
                      placeholder="https://facebook.com/yourpage"
                      className="form-input"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9CA3AF' }}>
                      Instagram URL
                    </label>
                    <input
                      type="url"
                      value={socialLinks.instagram}
                      onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                      placeholder="https://instagram.com/yourprofile"
                      className="form-input"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9CA3AF' }}>
                      Twitter URL
                    </label>
                    <input
                      type="url"
                      value={socialLinks.twitter}
                      onChange={(e) => setSocialLinks({ ...socialLinks, twitter: e.target.value })}
                      placeholder="https://twitter.com/yourhandle"
                      className="form-input"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9CA3AF' }}>
                      LinkedIn URL
                    </label>
                    <input
                      type="url"
                      value={socialLinks.linkedin}
                      onChange={(e) => setSocialLinks({ ...socialLinks, linkedin: e.target.value })}
                      placeholder="https://linkedin.com/company/yourcompany"
                      className="form-input"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9CA3AF' }}>
                      YouTube URL
                    </label>
                    <input
                      type="url"
                      value={socialLinks.youtube}
                      onChange={(e) => setSocialLinks({ ...socialLinks, youtube: e.target.value })}
                      placeholder="https://youtube.com/@yourchannel"
                      className="form-input"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary"
                    style={{ width: 'fit-content' }}
                  >
                    {loading ? 'Saving...' : 'Save Social Media Links'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );


      case 'blog':
        return <AdminBlogEditor adminToken={localStorage.getItem('admin_token')} />;

      case 'campaigns':
        return <AdminCampaigns adminToken={localStorage.getItem('admin_token')} />;

      default:
        return (
          <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
            <Clock size={64} color="#9CA3AF" style={{ margin: '0 auto 1.5rem' }} />
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Feature Coming Soon</h3>
            <p style={{ color: '#9CA3AF' }}>This admin panel section is under development.</p>
          </div>
        );
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0A0F2A' }}>
      {/* Admin Sidebar */}
      <div style={{ 
        width: '280px', 
        background: '#151827', 
        borderRight: '2px solid #2D3748',
        padding: '2rem 1rem',
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto'
      }}>
        <div style={{ marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '1px solid #2D3748' }}>
          <h1 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 700,
            background: 'linear-gradient(90deg, #DC2626 0%, #F59E0B 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            ADMIN CONSOLE
          </h1>
          <p style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.5rem' }}>Master Control Panel</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {ADMIN_MENU.map(item => (
            <button
              key={item.id}
              onClick={() => setActivePanel(item.id)}
              className={activePanel === item.id ? 'btn btn-primary' : 'btn btn-ghost'}
              style={{ 
                justifyContent: 'flex-start', 
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}
            >
              <item.icon size={20} />
              {item.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => navigate('/dashboard')}
          className="btn btn-ghost"
          style={{ width: '100%', marginTop: '2rem' }}
        >
          Exit Admin
        </button>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: '2rem', overflowY: 'auto' }}>
        <motion.div
          key={activePanel}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          {renderPanelContent()}
        </motion.div>
      </div>
    </div>
  );
}

export default AdminConsole;
